import { processListingParams } from '@/api/helpers';
import {
  AwarenessCompletions,
  CourseScoreByTagReport,
  GroupedOverdueCoursesReport,
  ListAwarenessCompletionsReportResponse,
  ListCompletionsOverTimeReportResponse,
  ListGroupedOverdueCoursesReportResponse,
  ListOverdueUsersReportResponse,
  ListPhishingClickRateReportResponse,
  ListSCSurveyCompletionsReportResponse,
  ListSCSurveyScoreReportResponse,
  PhishingClickRate,
  ProductActivityOvertimeReport,
  SCSurveyCompletions,
  SCSurveyScore,
} from '@/client/dashboard/types';
import { getExceptionFromAxiosError } from '@/client/helpers';
import { GroupedByEntityFilterType, ReportRequest } from '@/client/reports';
import { TagsRequest } from '@/client/tags';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

export const dashboardService = (client: AxiosInstance) => {
  const DASHBOARD_REPORTS_BASE_URL = (accountId?: string) =>
    `/accounts/${accountId}/kpi`;

  const getCourseCompletionsReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<AwarenessCompletions[]> => {
    try {
      const result = await client.get<
        ListAwarenessCompletionsReportResponse,
        AxiosResponse<ListAwarenessCompletionsReportResponse>
      >(`${DASHBOARD_REPORTS_BASE_URL(accountId)}/awareness-completions`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data.result;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getPhishingClickRateReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<PhishingClickRate[]> => {
    try {
      const result = await client.get<
        ListPhishingClickRateReportResponse,
        AxiosResponse<ListPhishingClickRateReportResponse>
      >(`${DASHBOARD_REPORTS_BASE_URL(accountId)}/phishing-click-rate`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data.result;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getSCSurveyCompletionsReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<SCSurveyCompletions[]> => {
    try {
      const result = await client.get<
        ListSCSurveyCompletionsReportResponse,
        AxiosResponse<ListSCSurveyCompletionsReportResponse>
      >(
        `${DASHBOARD_REPORTS_BASE_URL(
          accountId,
        )}/security-culture-survey-completion`,
        {
          withCredentials: true,
          params: processListingParams(params),
        },
      );

      return result.data.result;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getSCSurveyScoreReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<SCSurveyScore[]> => {
    try {
      const result = await client.get<
        ListSCSurveyScoreReportResponse,
        AxiosResponse<ListSCSurveyScoreReportResponse>
      >(
        `${DASHBOARD_REPORTS_BASE_URL(
          accountId,
        )}/security-culture-survey-score`,
        {
          withCredentials: true,
          params: processListingParams(params),
        },
      );

      return result.data.result;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getProductActivityOverTimeReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<ProductActivityOvertimeReport[]> => {
    try {
      const result = await client.get<
        ProductActivityOvertimeReport[],
        AxiosResponse<ProductActivityOvertimeReport[]>
      >(`${DASHBOARD_REPORTS_BASE_URL(accountId)}/activity`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getUsersWithOverdueCoursesReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<ListOverdueUsersReportResponse> => {
    try {
      const result = await client.get<
        ListOverdueUsersReportResponse,
        AxiosResponse<ListOverdueUsersReportResponse>
      >(`${DASHBOARD_REPORTS_BASE_URL(accountId)}/overdue-courses-per-month`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCompletionsOverTimeReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<ListCompletionsOverTimeReportResponse> => {
    try {
      const result = await client.get<
        ListCompletionsOverTimeReportResponse,
        AxiosResponse<ListCompletionsOverTimeReportResponse>
      >(
        `${DASHBOARD_REPORTS_BASE_URL(accountId)}/completed-courses-per-month`,
        {
          withCredentials: true,
          params: processListingParams(params),
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  // AWT dashboard
  const getGroupedOverdueCoursesReport = async (
    accountId: string,
    params: ReportRequest & {
      groupBy: GroupedByEntityFilterType;
    },
  ): Promise<GroupedOverdueCoursesReport[]> => {
    try {
      const result = await client.get<
        ListGroupedOverdueCoursesReportResponse,
        AxiosResponse<ListGroupedOverdueCoursesReportResponse>
      >(`${DASHBOARD_REPORTS_BASE_URL(accountId)}/grouped-overdue-courses`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data.result;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCourseScoreByTagReport = async (
    accountId: string,
    params: ReportRequest & TagsRequest,
  ): Promise<CourseScoreByTagReport[]> => {
    try {
      const result = await client.get<
        CourseScoreByTagReport[],
        AxiosResponse<CourseScoreByTagReport[]>
      >(`${DASHBOARD_REPORTS_BASE_URL(accountId)}/course-scores-by-tags`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  return {
    getCourseCompletionsReport,
    getPhishingClickRateReport,
    getSCSurveyCompletionsReport,
    getSCSurveyScoreReport,
    getProductActivityOverTimeReport,
    getUsersWithOverdueCoursesReport,
    getCompletionsOverTimeReport,
    getGroupedOverdueCoursesReport,
    getCourseScoreByTagReport,
  };
};
